import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { cognitoClient, cognitoConfig } from '../config/cognitoClient';
import {
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from '@aws-sdk/client-cognito-identity-provider';

interface AuthUser {
  userId: string;
  email: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  signIn: (email: string) => Promise<void>;
  confirmSignIn: (code: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEYS = {
  idToken: 'cognito_id_token',
  accessToken: 'cognito_access_token',
  refreshToken: 'cognito_refresh_token',
} as const;

function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1];
  if (!base64Url) throw new Error('Invalid JWT token');
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
  return JSON.parse(jsonPayload);
}

function getUserFromIdToken(idToken: string): AuthUser | null {
  try {
    const payload = decodeJwtPayload(idToken);
    return {
      userId: (payload.sub as string) || '',
      email: (payload.email as string) || '',
    };
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  try {
    const payload = decodeJwtPayload(token);
    const exp = payload.exp as number;
    // Add 60s buffer
    return Date.now() >= (exp - 60) * 1000;
  } catch {
    return true;
  }
}

function clearTokens() {
  localStorage.removeItem(TOKEN_KEYS.idToken);
  localStorage.removeItem(TOKEN_KEYS.accessToken);
  localStorage.removeItem(TOKEN_KEYS.refreshToken);
}

function storeTokens(authResult: {
  IdToken?: string;
  AccessToken?: string;
  RefreshToken?: string;
}) {
  if (authResult.IdToken)
    localStorage.setItem(TOKEN_KEYS.idToken, authResult.IdToken);
  if (authResult.AccessToken)
    localStorage.setItem(TOKEN_KEYS.accessToken, authResult.AccessToken);
  if (authResult.RefreshToken)
    localStorage.setItem(TOKEN_KEYS.refreshToken, authResult.RefreshToken);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Store session, email, and Cognito username between signIn and confirmSignIn
  const sessionRef = useRef<string | null>(null);
  const emailRef = useRef<string | null>(null);
  const usernameRef = useRef<string | null>(null);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem(TOKEN_KEYS.refreshToken);
    if (!refreshToken) return false;

    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: cognitoConfig.userPoolClientId,
        AuthParameters: {
          REFRESH_TOKEN: refreshToken,
        },
      });

      const response = await cognitoClient.send(command);
      const result = response.AuthenticationResult;

      if (result?.IdToken) {
        storeTokens(result);
        const parsed = getUserFromIdToken(result.IdToken);
        if (parsed) {
          setUser(parsed);
          return true;
        }
      }
    } catch (err) {
      console.error('Failed to refresh session:', err);
    }

    return false;
  }, []);

  // Restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const idToken = localStorage.getItem(TOKEN_KEYS.idToken);
        if (!idToken) {
          setIsLoading(false);
          return;
        }

        if (!isTokenExpired(idToken)) {
          const parsed = getUserFromIdToken(idToken);
          if (parsed) {
            setUser(parsed);
            setIsLoading(false);
            return;
          }
        }

        // Token expired, try refresh
        const refreshed = await refreshSession();
        if (!refreshed) {
          clearTokens();
        }
      } catch {
        clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, [refreshSession]);

  // Proactive token refresh — check every 5 minutes, refresh if expiring soon
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(
      async () => {
        const idToken = localStorage.getItem(TOKEN_KEYS.idToken);
        if (idToken && isTokenExpired(idToken)) {
          const refreshed = await refreshSession();
          if (!refreshed) {
            clearTokens();
            setUser(null);
          }
        }
      },
      5 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, [user, refreshSession]);

  const signIn = useCallback(async (email: string) => {
    setError(null);

    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'USER_AUTH',
        ClientId: cognitoConfig.userPoolClientId,
        AuthParameters: {
          USERNAME: email,
          PREFERRED_CHALLENGE: 'EMAIL_OTP',
        },
      });

      let response = await cognitoClient.send(command);

      // Handle SELECT_CHALLENGE: Cognito wants us to pick a challenge type
      if (response.ChallengeName === 'SELECT_CHALLENGE' && response.Session) {
        const selectCommand = new RespondToAuthChallengeCommand({
          ClientId: cognitoConfig.userPoolClientId,
          ChallengeName: 'SELECT_CHALLENGE',
          Session: response.Session,
          ChallengeResponses: {
            USERNAME: email,
            ANSWER: 'EMAIL_OTP',
          },
        });
        response = await cognitoClient.send(selectCommand);
      }

      if (response.ChallengeName === 'EMAIL_OTP' && response.Session) {
        sessionRef.current = response.Session;
        emailRef.current = email;
        usernameRef.current = response.ChallengeParameters?.USERNAME ?? email;
      } else {
        throw new Error(
          `Unexpected challenge: ${response.ChallengeName || 'none'}`,
        );
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to send verification code';
      setError(message);
      throw err;
    }
  }, []);

  const confirmSignIn = useCallback(async (code: string) => {
    setError(null);

    if (!sessionRef.current || !emailRef.current) {
      const msg = 'No active sign-in session. Please request a new code.';
      setError(msg);
      throw new Error(msg);
    }

    try {
      const command = new RespondToAuthChallengeCommand({
        ClientId: cognitoConfig.userPoolClientId,
        ChallengeName: 'EMAIL_OTP',
        Session: sessionRef.current,
        ChallengeResponses: {
          USERNAME: usernameRef.current!,
          EMAIL_OTP_CODE: code,
        },
      });

      const response = await cognitoClient.send(command);
      const result = response.AuthenticationResult;

      if (!result?.IdToken) {
        throw new Error('Authentication failed: no tokens received');
      }

      storeTokens(result);

      const parsed = getUserFromIdToken(result.IdToken);
      if (!parsed) {
        throw new Error('Failed to parse user information from token');
      }

      setUser(parsed);
      sessionRef.current = null;
      emailRef.current = null;
      usernameRef.current = null;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to verify code';
      setError(message);
      throw err;
    }
  }, []);

  const signOut = useCallback(() => {
    clearTokens();
    setUser(null);
    setError(null);
    sessionRef.current = null;
    emailRef.current = null;
    usernameRef.current = null;
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    signIn,
    confirmSignIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
